import {
  AddIcon,
  Button,
  DatePicker,
  Grid,
  GridColumn,
  GridRow,
  Input,
  Select,
  type SelectOptionType,
  TextArea,
} from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { type ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AvailableService } from '../../../../hooks/useAvailableServicesForOrganization';
import { useCreateSelectOptions } from '../../../../hooks/useCreateSelectOptions';
import type { TErrors } from '../../../AppointmentForm/AppointmentForm';
import { BpCard } from '../../../BpCard/BpCard';
import { VirtualMeetingSettings } from './VirtualMeetingSettings';

const AppointmentMetaFieldsDocument = graphql(`
  fragment AppointmentMetaFields on Query {
    calendarEvents(options: { sort: [{ start: ASC }], limit: 500 }, where: { uuid: $appointmentUuid }) {
      uuid
      __typename
      categories
    }
    teachingUnits(where: { group: { uuid: $holderUuid } }, options: { sort: [{ title: ASC }] }) {
      uuid
      __typename
      title
    }
  }
`);

type AppointmentMetaProps = {
  availableServices: AvailableService[];
  canAssignTeachingUnit?: boolean;
  disableEventTypeValues: string[];
  query: FragmentType<typeof AppointmentMetaFieldsDocument> | undefined;
};

export const AppointmentMeta = ({
  query,
  availableServices,
  canAssignTeachingUnit,
  disableEventTypeValues,
}: AppointmentMetaProps) => {
  const { t } = useTranslation();
  const fragment = getFragmentData(AppointmentMetaFieldsDocument, query);
  const calendarEvent = fragment?.calendarEvents[0];
  const teachingUnits = fragment?.teachingUnits;

  const { setFieldValue, setFieldTouched, handleChange, values, errors, initialValues } = useFormikContext<TErrors>();

  const [textAreaVisible, setTextAreaVisible] = useState(values.desc !== '');

  const selectOptsTeachingUnit = useCreateSelectOptions(teachingUnits, 'uuid', 'title');

  // biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
  const eventTypeOpts = useMemo(
    () =>
      [
        { value: 'event', label: t('events.categories.event') },
        { value: 'lesson', label: t('events.categories.lesson') },
        { value: 'exam', label: t('events.categories.exam') },
      ].filter((e) => !disableEventTypeValues.includes(e.value)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableEventTypeValues],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
  const locationTypeSelectOpts = useMemo(() => {
    if (availableServices.length > 0) {
      return [
        { value: 'onSite', label: t('events.types.onSite') },
        { value: 'hybrid', label: t('events.types.hybrid', { context: 'long' }) },
        { value: 'virtual', label: t('events.types.virtual', { context: 'long' }) },
      ];
    } else {
      return [{ value: 'onSite', label: t('events.types.onSite') }];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableServices]);

  const onEventTypeChange = async (ev: SelectOptionType) => {
    let categories = values.categories;
    const v = eventTypeOpts.map((item) => item.value);
    categories = categories?.filter((el) => !v.includes(el));
    await setFieldValue('categories', [...categories, ev.value]);
  };

  const onLocationChange = async (ev: SelectOptionType) => {
    let categories = values.categories;
    const v = locationTypeSelectOpts.map((item) => item.value);
    categories = categories.filter((el) => !v.includes(el));
    if (ev.value === 'hybrid') {
      await setFieldValue('categories', [...categories, 'onSite', 'virtual']);
    } else if (ev.value === 'onSite') {
      await setFieldValue('service', { value: '', label: '' });
      await setFieldValue('participantsCount', initialValues.participantsCount);
      await setFieldValue('duration', initialValues.duration);
      await setFieldValue('categories', [...categories, ev.value]);
    } else {
      await setFieldValue('categories', [...categories, ev.value]);
    }
  };

  const isVirtual = values.categories.includes('virtual');
  const isOnsite = values.categories.includes('onSite');
  const isHybrid = isVirtual && isOnsite;

  const onTeachingUnitChange = async (ev: SelectOptionType) => {
    await setFieldValue('teachingUnit', ev?.value);
  };

  return (
    <BpCard header={{ headline: t('appointments.general') }}>
      <Select
        className='mb-2'
        options={eventTypeOpts}
        onChange={async (e) => {
          await onEventTypeChange(e as unknown as SelectOptionType);
        }}
        defaultValue={
          eventTypeOpts.find((opt) =>
            calendarEvent?.categories ? calendarEvent?.categories.includes(opt.value as string) : false,
          ) ?? eventTypeOpts.find((opt) => opt.value === 'event')
        }
        name={'type'}
        label={t('common.type')}
        error={errors.lessonType}
        menuPosition='fixed'
      />

      {textAreaVisible && (
        <TextArea
          className='mb-2'
          label={t('common.description')}
          onChange={handleChange}
          value={values.desc}
          name='desc'
          rows={4}
          isScrollable
        />
      )}

      {!textAreaVisible && (
        <Button
          className='mb-5 mt-2'
          onClick={() => setTextAreaVisible(true)}
          icon={<AddIcon className='svg-icon white' />}
        >
          {t('common.addType', { type: t('common.description') })}
        </Button>
      )}

      <Grid useFormGap>
        <GridRow spacingBottom='none' breakpoint='phone'>
          <GridColumn width={6}>
            <DatePicker
              label={t('appointments.from')}
              onChange={async (e) => {
                await setFieldValue('startDate', dayjs(e));
                await setFieldTouched('startDate', true);

                // also change date of startTime
                const newTime = dayjs(dayjs(e).format('YYYY-MM-DD') + values.startTime.format('HH:mm'));
                await setFieldValue('startTime', newTime, true);
              }}
              name={'startDate'}
              error={errors.startDate as string}
              value={values.startDate.toDate()}
              showMonthYearDropdown
            />
          </GridColumn>
          <GridColumn width={6}>
            <Input
              name={`startTime`}
              label={t('common.start')}
              error={errors.startTime as string}
              value={values.startTime.format('HH:mm')}
              onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                const newTime = dayjs(values.startDate.format('YYYY-MM-DD') + event.target.value);
                await setFieldValue('startTime', newTime, true);
                await setFieldTouched('startTime', true);
              }}
              type='time'
            />
          </GridColumn>
        </GridRow>
        <GridRow spacingBottom='none' spacingTop='none' breakpoint='phone'>
          <GridColumn width={6}>
            <DatePicker
              label={t('appointments.to')}
              error={errors.endDate as string}
              onChange={async (e) => {
                await setFieldValue('endDate', dayjs(e), true);
                await setFieldTouched('endDate', true);

                // also change date of endTime
                const newTime = dayjs(dayjs(e).format('YYYY-MM-DD') + values.endTime.format('HH:mm'));
                await setFieldValue('endTime', newTime, true);
              }}
              name={'endDate'}
              value={values.endDate.toDate()}
              showMonthYearDropdown
            />
          </GridColumn>
          <GridColumn width={6}>
            <Input
              name={`endTime`}
              label={t('common.end')}
              error={errors.endTime as string}
              value={values.endTime.format('HH:mm')}
              onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                const newTime = dayjs(values.endDate.format('YYYY-MM-DD') + event.target.value);
                await setFieldValue('endTime', newTime, true);
                await setFieldTouched('endTime', true);
              }}
              type='time'
            />
          </GridColumn>
        </GridRow>
      </Grid>

      {availableServices.length > 0 && (
        <Select
          className='mb-2'
          options={locationTypeSelectOpts}
          onChange={async (e) => {
            await onLocationChange(e as unknown as SelectOptionType);
            await setFieldTouched('lessonType');
          }}
          label={t('appointments.appointmentsType')}
          name={'lessonType'}
          defaultValue={
            locationTypeSelectOpts.find((opt) => {
              return isHybrid ? opt.value === 'hybrid' : isVirtual ? opt.value === 'virtual' : opt.value === 'onSite';
            }) ?? locationTypeSelectOpts[0]
          }
          menuPosition='fixed'
        />
      )}

      {(values.categories.includes('virtual') || values.categories.includes('hybrid')) && (
        <VirtualMeetingSettings availableServices={availableServices} />
      )}

      {canAssignTeachingUnit ? (
        <Select
          options={selectOptsTeachingUnit}
          value={selectOptsTeachingUnit.find((tu) => {
            return tu.value === values.teachingUnit;
          })}
          onChange={async (e) => {
            await onTeachingUnitChange(e as unknown as SelectOptionType);
          }}
          label={t('teachingUnits.titleSingular')}
          name={'teachingUnit'}
          menuPosition='fixed'
        />
      ) : (
        <></>
      )}
    </BpCard>
  );
};
